import { createRouter, createWebHistory } from 'vue-router'
import { installGuards } from './guards'

// Eager imports — same two views were eager in old admin (router.js)
import TenantLogin from '@/views/TenantLogin.vue'
import UserLogin from '@/views/UserLogin.vue'

const PrintTest = () => import('@/views/PrintTest.vue')
const InvoiceScan = () => import('@/views/InvoiceScan.vue')

const AdminLayout = () => import('@/views/admin/AdminLayout.vue')
const Dashboard = () => import('@/views/admin/dashboard/Dashboard.vue')
const Clients = () => import('@/views/admin/clients/Clients.vue')
const ClientTransactionsSummary = () => import('@/views/admin/clients/ClientTransactionsSummary.vue')
const ClientTransactions = () => import('@/views/admin/clients/ClientTransactions.vue')
const BankStatements = () => import('@/views/admin/bankStatements/BankStatements.vue')
const BankStatement = () => import('@/views/admin/bankStatements/BankStatement.vue')
const OrderEdit = () => import('@/views/admin/orders/OrderEdit.vue')
const AccountLayout = () => import('@/views/admin/account/AccountLayout.vue')
const AccountProfile = () => import('@/views/admin/account/AccountProfile.vue')
const AccountCompany = () => import('@/views/admin/account/AccountCompany.vue')
const AccountReceipts = () => import('@/views/admin/account/AccountReceipts.vue')
const AccountIntegrations = () => import('@/views/admin/account/AccountIntegrations.vue')
const AccountSettings = () => import('@/views/admin/account/AccountSettings.vue')
const Billing = () => import('@/views/admin/Billing.vue')
const Dispatches = () => import('@/views/admin/Dispatches.vue')
const EDispatches = () => import('@/views/admin/EDispatches.vue')
const EReceptions = () => import('@/views/admin/EReceptions.vue')
const OrderItemComments = () => import('@/views/admin/OrderItemComments.vue')
const Products = () => import('@/views/admin/Products.vue')
const ProductsPriceTags = () => import('@/views/admin/ProductsPriceTags.vue')
const ProductPrices = () => import('@/views/admin/ProductPrices.vue')
const Pricelists = () => import('@/views/admin/Pricelists.vue')
const Orders = () => import('@/views/admin/orders/Orders.vue')
const TaxRates = () => import('@/views/admin/TaxRates.vue')
const Receptions = () => import('@/views/admin/Receptions.vue')
const ReceptionEdit = () => import('@/views/admin/ReceptionEdit.vue')
const DispatchEdit = () => import('@/views/admin/DispatchEdit.vue')
const EReceptionEdit = () => import('@/views/admin/EReceptionEdit.vue')
const EDispatchEdit = () => import('@/views/admin/EDispatchEdit.vue')
const Reports = () => import('@/views/admin/Reports.vue')
const TablesSettings = () => import('@/views/admin/TablesSettings.vue')
const OrderSettings = () => import('@/views/admin/OrderSettings.vue')
const Terminals = () => import('@/views/admin/terminals/Terminals.vue')
const BeginningInventories = () => import('@/views/admin/beginningInventories/BeginningInventories.vue')
const BeginningInventory = () => import('@/views/admin/beginningInventories/BeginningInventory.vue')
const PriceCalculations = () => import('@/views/admin/priceCalculations/PriceCalculations.vue')
const PriceCalculation = () => import('@/views/admin/priceCalculations/PriceCalculation.vue')
const PriceLevelings = () => import('@/views/admin/priceLevelings/PriceLevelings.vue')
const PriceLeveling = () => import('@/views/admin/priceLevelings/PriceLeveling.vue')
const WriteOffs = () => import('@/views/admin/writeOffs/WriteOffs.vue')
const WriteOff = () => import('@/views/admin/writeOffs/WriteOff.vue')
const Stock = () => import('@/views/admin/stock/Stock.vue')
const StockItem = () => import('@/views/admin/stock/StockItem.vue')
const SalesPurchaseLedger = () => import('@/views/admin/salesPurchaseLedger/SalesPurchaseLedger.vue')
const CashSales = () => import('@/views/admin/cashSales/CashSales.vue')
const UserRoles = () => import('@/views/admin/UserRoles.vue')
const UserRoleEdit = () => import('@/views/admin/UserRoleEdit.vue')
const Users = () => import('@/views/admin/Users.vue')
const WorkingHours = () => import('@/views/admin/WorkingHours.vue')
const Categories = () => import('@/views/admin/Categories.vue')

const Barcodes = () => import('@/views/admin/Barcodes.vue')
const Shortcuts = () => import('@/views/admin/Shortcuts.vue')
const BeachSettings = () => import('@/views/admin/BeachSettings.vue')
const OrderSources = () => import('@/views/admin/OrderSources.vue')

// Route table is a 1:1 port of apps/admin/src/router/router.js — same paths, names,
// permissions and module gates. Commented-out requiredPermission lines are preserved
// from the original on purpose (they document intentionally disabled permissions).
const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            redirect: '/login',
        },
        {
            path: '/print-test',
            name: 'printTest',
            component: PrintTest,
        },
        {
            path: '/invoice-scan/:token',
            name: 'invoiceScan',
            component: InvoiceScan,
        },
        {
            path: '/login',
            name: 'tenantLogin',
            component: TenantLogin,
        },
        {
            path: '/user-login',
            name: 'userLogin',
            component: UserLogin,
        },
        {
            path: '/admin',
            name: 'admin',
            redirect: '/admin/dashboard',
            component: AdminLayout,
            meta: { requiredPermission: 'nav_access_admin' },
            children: [
                {
                    path: 'dashboard',
                    name: 'adminDashboard',
                    component: Dashboard,
                    meta: { requiredPermission: 'access_dashboard' },
                },
                {
                    path: 'users',
                    name: 'adminUsers',
                    component: Users,
                    meta: { requiredPermission: 'access_users' },
                },
                {
                    path: 'user-groups',
                    name: 'adminUserRoles',
                    component: UserRoles,
                    meta: { requiredPermission: 'access_user_roles' },
                },
                {
                    path: 'user-groups/new',
                    name: 'adminUserRoleCreate',
                    component: UserRoleEdit,
                    meta: { requiredPermission: 'access_user_roles' },
                },
                {
                    path: 'user-groups/:id',
                    name: 'adminUserRoleUpdate',
                    component: UserRoleEdit,
                    meta: { requiredPermission: 'access_user_roles' },
                },
                {
                    path: 'inventory',
                    name: 'adminProducts',
                    component: Products,
                    meta: { requiredPermission: 'access_products' },
                },
                {
                    // Artikli's Cene tab: the price mode (docs/QuickPriceEdit.md).
                    path: 'inventory/prices',
                    name: 'adminProductPrices',
                    component: ProductPrices,
                    meta: { requiredPermission: ['access_products', 'allow_products_edit'] },
                },
                {
                    path: 'inventory/price-tags',
                    name: 'adminProductsPriceTags',
                    component: ProductsPriceTags,
                    meta: { requiredPermission: 'access_products' },
                },
                {
                    path: 'pricelists',
                    name: 'adminPricelists',
                    component: Pricelists,
                    meta: { requiredPermission: 'access_products', enabledWithModules: ['pricelists'] },
                },
                {
                    path: 'order-item-comments',
                    name: 'adminOrderItemComments',
                    component: OrderItemComments,
                    meta: { requiredPermission: 'access_order_item_comments' },
                },
                {
                    path: 'categories',
                    name: 'adminProductCategories',
                    component: Categories,
                    meta: { requiredPermission: 'access_product_categories' },
                },
                {
                    path: 'delivery-in-notes',
                    name: 'adminReceptions',
                    component: Receptions,
                    meta: { requiredPermission: 'access_receptions' },
                },
                // Delivery-note editor pages (document editors): a required `:id` beside `/new`,
                // so the bare list URL never resolves to the editor (see test/router.spec.ts).
                {
                    path: 'delivery-in-notes/new',
                    name: 'adminReceptionCreate',
                    component: ReceptionEdit,
                    meta: { requiredPermission: 'access_receptions', inventoryAccountingLocked: 'adminReceptions' },
                },
                {
                    path: 'delivery-in-notes/:id',
                    name: 'adminReceptionUpdate',
                    component: ReceptionEdit,
                    meta: { requiredPermission: 'access_receptions', inventoryAccountingLocked: 'adminReceptions' },
                },
                {
                    path: 'e-delivery-in-notes',
                    name: 'adminEReceptions',
                    component: EReceptions,
                    meta: { requiredPermission: 'access_receptions', requiredIntegration: 'edispatch' },
                },
                {
                    path: 'e-delivery-in-notes/new',
                    name: 'adminEReceptionCreate',
                    component: EReceptionEdit,
                    meta: { requiredPermission: 'access_receptions', requiredIntegration: 'edispatch' },
                },
                {
                    path: 'e-delivery-in-notes/:id',
                    name: 'adminEReceptionUpdate',
                    component: EReceptionEdit,
                    meta: { requiredPermission: 'access_receptions', requiredIntegration: 'edispatch' },
                },
                {
                    path: 'delivery-out-notes',
                    name: 'adminDispatches',
                    component: Dispatches,
                    meta: { requiredPermission: 'access_dispatches' },
                },
                {
                    path: 'delivery-out-notes/new',
                    name: 'adminDispatchCreate',
                    component: DispatchEdit,
                    meta: { requiredPermission: 'access_dispatches', inventoryAccountingLocked: 'adminDispatches' },
                },
                {
                    path: 'delivery-out-notes/:id',
                    name: 'adminDispatchUpdate',
                    component: DispatchEdit,
                    meta: { requiredPermission: 'access_dispatches', inventoryAccountingLocked: 'adminDispatches' },
                },
                {
                    path: 'e-delivery-out-notes',
                    name: 'adminEDispatches',
                    component: EDispatches,
                    meta: { requiredPermission: 'access_dispatches', requiredIntegration: 'edispatch' },
                },
                {
                    path: 'e-delivery-out-notes/new',
                    name: 'adminEDispatchCreate',
                    component: EDispatchEdit,
                    meta: { requiredPermission: 'access_dispatches', requiredIntegration: 'edispatch' },
                },
                {
                    path: 'e-delivery-out-notes/:id',
                    name: 'adminEDispatchUpdate',
                    component: EDispatchEdit,
                    meta: { requiredPermission: 'access_dispatches', requiredIntegration: 'edispatch' },
                },
                {
                    path: 'beginning-inventories',
                    name: 'adminBeginningInventories',
                    component: BeginningInventories,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_beginning_inventories' },
                },
                {
                    path: 'beginning-inventories/new',
                    name: 'adminBeginningInventoryCreate',
                    component: BeginningInventory,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_beginning_inventories' },
                },
                {
                    path: 'beginning-inventories/:id',
                    name: 'adminBeginningInventoryUpdate',
                    component: BeginningInventory,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_beginning_inventories' },
                },
                {
                    path: 'price-calculations',
                    name: 'adminPriceCalculations',
                    component: PriceCalculations,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_price_calculations' },
                },
                {
                    path: 'price-calculations/new',
                    name: 'adminPriceCalculationCreate',
                    component: PriceCalculation,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_price_calculations' },
                },
                {
                    path: 'price-calculations/:id',
                    name: 'adminPriceCalculationUpdate',
                    component: PriceCalculation,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_price_calculations' },
                },
                {
                    path: 'price-levelings',
                    name: 'adminPriceLevelings',
                    component: PriceLevelings,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_price_levelings' },
                },
                {
                    path: 'price-levelings/new',
                    name: 'adminPriceLevelingCreate',
                    component: PriceLeveling,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_price_levelings' },
                },
                {
                    path: 'price-levelings/:id',
                    name: 'adminPriceLevelingUpdate',
                    component: PriceLeveling,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_price_levelings' },
                },
                {
                    path: 'write-offs',
                    name: 'adminWriteOffs',
                    component: WriteOffs,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_write_offs' },
                },
                {
                    path: 'write-offs/new',
                    name: 'adminWriteOffCreate',
                    component: WriteOff,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_write_offs' },
                },
                {
                    path: 'write-offs/:id',
                    name: 'adminWriteOffUpdate',
                    component: WriteOff,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_write_offs' },
                },
                {
                    path: 'stock',
                    name: 'adminStock',
                    component: Stock,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_stock' },
                },
                {
                    path: 'stock-item/:id?',
                    name: 'adminStockItem',
                    component: StockItem,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_stock_item' },
                },
                {
                    path: 'kep',
                    name: 'adminSalesPurchaseLedger',
                    component: SalesPurchaseLedger,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_sales_purchase_ledger' },
                },
                {
                    path: 'cash-sales',
                    name: 'adminCashSales',
                    component: CashSales,
                    meta: { enabledWithModules: ['inventory_accounting'] },
                    // meta: { requiredPermission: 'access_cash_sales' },
                },
                {
                    path: 'orders',
                    name: 'adminOrders',
                    component: Orders,
                    meta: { requiredPermission: 'access_orders' },
                },
                {
                    path: 'reports',
                    name: 'adminReports',
                    component: Reports,
                    meta: { requiredPermission: 'access_reports' },
                },
                {
                    path: 'working-hours',
                    name: 'adminWorkingHours',
                    component: WorkingHours,
                    meta: { requiredPermission: 'access_working_hours' },
                },
                {
                    path: 'tables-settings',
                    name: 'adminTablesSettings',
                    component: TablesSettings,
                    meta: { requiredPermission: 'access_tables_settings' },
                },
                {
                    path: 'order-settings',
                    name: 'adminOrderSettings',
                    component: OrderSettings,
                    meta: { requiredPermission: 'access_order_settings' },
                },
                {
                    path: 'terminals',
                    name: 'adminTerminals',
                    component: Terminals,
                    meta: { requiredPermission: 'access_terminals' },
                },
                {
                    path: 'tax-rates',
                    name: 'adminTaxRates',
                    component: TaxRates,
                    meta: { requiredPermission: 'access_tax_rates' },
                },
                {
                    path: 'clients',
                    name: 'adminClients',
                    component: Clients,
                    meta: { requiredPermission: 'access_clients' },
                },
                {
                    path: 'client-transactions/summary',
                    name: 'adminClientTransactionsSummary',
                    component: ClientTransactionsSummary,
                    meta: {
                        requiredPermission: 'access_client_transactions',
                        enabledWithModules: ['inventory_accounting', 'einvoices'],
                    },
                },
                {
                    path: 'client-transactions/:client_id?',
                    name: 'adminClientTransactions',
                    component: ClientTransactions,
                    meta: {
                        requiredPermission: 'access_client_transactions',
                        enabledWithModules: ['inventory_accounting', 'einvoices'],
                    },
                },
                {
                    path: 'bank-statements',
                    name: 'adminBankStatements',
                    component: BankStatements,
                    meta: {
                        requiredPermission: 'access_bank_statements',
                        enabledWithModules: ['inventory_accounting', 'einvoices'],
                    },
                },
                {
                    path: 'bank-statements/new',
                    name: 'adminBankStatementCreate',
                    component: BankStatement,
                    meta: {
                        requiredPermission: 'access_bank_statements',
                        enabledWithModules: ['inventory_accounting', 'einvoices'],
                    },
                },
                {
                    path: 'bank-statements/:id',
                    name: 'adminBankStatementUpdate',
                    component: BankStatement,
                    meta: {
                        requiredPermission: 'access_bank_statements',
                        enabledWithModules: ['inventory_accounting', 'einvoices'],
                    },
                },
                {
                    path: 'order/new',
                    name: 'adminOrderCreate',
                    component: OrderEdit,
                    meta: { requiredPermission: 'access_orders' }
                },
                {
                    path: 'order/:id?',
                    name: 'adminOrderUpdate',
                    component: OrderEdit,
                    meta: { requiredPermission: 'access_orders' }
                },
                {
                    path: 'barcodes',
                    name: 'adminBarcodes',
                    component: Barcodes,
                    meta: { requiredPermission: 'access_barcodes' },
                },
                {
                    path: 'shortcuts',
                    name: 'adminShortcuts',
                    component: Shortcuts,
                    meta: { requiredPermission: 'access_shortcuts' },
                },
                {
                    path: 'beach-settings',
                    name: 'adminBeachSettings',
                    component: BeachSettings,
                    meta: { requiredPermission: 'access_beach_settings', enabledWithModules: ['beach', 'beach_booking'] },
                },
                {
                    path: 'order-sources',
                    name: 'adminOrderSources',
                    component: OrderSources,
                    meta: { enabledWithModules: ['delivery'] },
                },
                {
                    // Account section split into routed pages — starter
                    // settings layout. Parent meta gates every child via to.matched.
                    path: 'account',
                    component: AccountLayout,
                    meta: { requiredPermission: 'access_account' },
                    children: [
                        { path: '', name: 'adminAccount', component: AccountProfile },
                        { path: 'company', name: 'adminAccountCompany', component: AccountCompany },
                        { path: 'receipts', name: 'adminAccountReceipts', component: AccountReceipts },
                        { path: 'integrations', name: 'adminAccountIntegrations', component: AccountIntegrations },
                        { path: 'settings', name: 'adminAccountSettings', component: AccountSettings },
                    ],
                },
                {
                    path: 'billing',
                    name: 'adminBilling',
                    component: Billing,
                    meta: { requiredPermission: 'access_account' },
                },
            ],
        },
    ],
})

installGuards(router)

export default router
